import type { CartItem, DeliveryKey, Order, PaymentMethodKey } from '@c214/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api';
import { useAuth } from './useAuth';

export const ORDERS_QUERY_KEY = ['orders'] as const;

export function useOrders() {
  const { data: user } = useAuth();
  return useQuery({
    queryKey: ORDERS_QUERY_KEY,
    queryFn: async () => (await apiGet<{ orders: Order[] }>('/api/orders')).orders,
    enabled: !!user,
  });
}

export interface CreateOrderInput {
  items: Pick<CartItem, 'productId' | 'weight' | 'grind' | 'qty'>[];
  deliveryKey: DeliveryKey;
  deliveryCity?: string;
  deliveryWarehouse?: string;
  deliveryStreet?: string;
  deliveryBuilding?: string;
  deliveryApartment?: string;
  deliveryFloor?: string;
  deliveryIntercom?: string;
  recipientLastName: string;
  recipientFirstName: string;
  recipientPatronymic?: string | null;
  recipientPhone: string;
  recipientIsSelf: boolean;
  paymentKey: PaymentMethodKey;
  comment?: string;
}

export interface CreateOrderResponse extends Order {
  checkoutUrl?: string;
}

export function useCreateOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrderInput) => apiPost<CreateOrderResponse>('/api/orders', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORDERS_QUERY_KEY }),
  });
}
